import axios, { AxiosRequestConfig } from 'axios';
import { CacheManager } from './CacheManager';

interface CachedRequestConfig extends AxiosRequestConfig {
  ttl?: number;
  skipCache?: boolean;
}

class CachedAxios {
  private cacheManager: CacheManager;
  private defaultTTL: number;

  constructor(defaultTTL: number = 60000) { // По умолчанию 1 минута
    this.cacheManager = new CacheManager();
    this.defaultTTL = defaultTTL;
  }

  public async get<T>(url: string, config: CachedRequestConfig = {}): Promise<T> {
    const { ttl = this.defaultTTL, skipCache = false, ...axiosConfig } = config;

    // Если установлен флаг skipCache, делаем запрос без кеширования
    if (skipCache) {
      const response = await axios.get<T>(url, axiosConfig);
      return response.data;
    }

    // Генерируем ключ для кеша
    const cacheKey = this.cacheManager.generateKey(url, axiosConfig.params);
    
    // Пробуем получить данные из кеша
    const cachedData = this.cacheManager.get<T>(cacheKey);
    if (cachedData !== null) {
      return cachedData;
    }

    // Если данных в кеше нет или они устарели, делаем запрос
    const response = await axios.get<T>(url, axiosConfig);
    
    // Сохраняем результат в кеш
    this.cacheManager.set(cacheKey, response.data, ttl);

    return response.data;
  }

  public async post<T>(url: string, data?: any, config: CachedRequestConfig = {}): Promise<T> {
    const { skipCache = false, ...axiosConfig } = config;

    // POST запросы всегда выполняются без кеширования
    const response = await axios.post<T>(url, data, axiosConfig);
    return response.data;
  }

  public clearCache(): void {
    this.cacheManager.clear();
  }

  public clearExpired(): void {
    this.cacheManager.clearExpired();
  }
}

// Создаем и экспортируем единственный экземпляр
export const cachedAxios = new CachedAxios(); 